import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, MapPin, Clock, Shield, CheckCircle, Calendar, Search, ArrowRight, List, MessageSquare, Video, MapPinIcon, Volume2, VolumeX, FileText, Handshake, Star, Lock, Zap, Heart, DollarSign, Quote } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useUserRole } from "@/hooks/useUserRole";
import { useConsultations } from "@/hooks/useConsultations";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.webp";
import mediatorIcon from "@/assets/logo.webp";
import mediationBg1 from "@/assets/mediation-bg-1.jpg?format=webp";
import mediationBg2 from "@/assets/mediation-bg-2.jpg?format=webp";
import mediationBg3 from "@/assets/mediation-bg-3.jpg?format=webp";
import voisinageHero from "@/assets/voisinage-hero.jpg?format=webp";
import voisinageCas from "@/assets/voisinage-cas.jpg?format=webp";
import travailHero from "@/assets/travail-hero.jpg?format=webp";
import familleHero from "@/assets/famille-hero.jpg?format=webp";
const heroVideo = "/hero-video.mp4";
import testimonialClaire from "@/assets/testimonial-claire.jpg?format=webp";
import testimonialThomas from "@/assets/testimonial-thomas.jpg?format=webp";
import testimonialSophie from "@/assets/testimonial-sophie-marc.jpg?format=webp";
import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { MediatorSearchWidget } from "@/components/MediatorSearchWidget";
import { Badge } from "@/components/ui/badge";
import SEO from "@/components/SEO";
import { OrganizationSchema, WebsiteSchema, HowToSchema, AggregateRatingSchema, VideoSchema } from "@/components/StructuredData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MessageThread } from "@/components/MessageThread";
import { getMediationAreaLabel } from "@/lib/mediationAreaMapping";
import { ConsultationCard } from "@/components/ConsultationCard";

const Index = () => {
  const navigate = useNavigate();
  const { user, signOut, loading } = useAuth();
  const { data: userRole } = useUserRole();
  const { data: consultations } = useConsultations(user?.id);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [showSearchWidget, setShowSearchWidget] = useState(false);
  const [activeSearchSection, setActiveSearchSection] = useState<string | null>(null);
  const searchWidgetRef = useRef<HTMLDivElement>(null);
  const sectionSearchRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const backgrounds = [mediationBg1, mediationBg2, mediationBg3];
  const videoRef = useRef<HTMLVideoElement>(null);

  // Attempt to autoplay video with sound when component mounts
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.play().catch(() => {
        // Browser blocked autoplay with sound, try muted autoplay as fallback
        video.muted = true;
        setIsVideoMuted(true);
        video.play().catch(() => {});
      });
    }
  }, []);

  const handleUnmute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      setIsVideoMuted(false);
    }
  };

  // Fetch mediator consultations for mediator home view
  const { data: mediatorConsultations } = useQuery({
    queryKey: ['mediatorHomeConsultations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('consultations')
        .select(`
          *,
          seeker:profiles!consultations_seeker_id_fkey(full_name, email, phone)
        `)
        .eq('mediator_id', user.id)
        .in('status', ['confirmed', 'paid', 'payment_completed'])
        .gte('scheduled_date', new Date().toISOString().split('T')[0])
        .order('scheduled_date', { ascending: true })
        .order('scheduled_time', { ascending: true })
        .limit(3);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && userRole === 'mediator',
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % backgrounds.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Redirect admin to their dashboard
  useEffect(() => {
    if (!loading && user && userRole === 'admin') {
      navigate("/admin");
    }
  }, [user, userRole, loading, navigate]);

  // Get next upcoming consultation for seeker
  const nextConsultation = consultations?.find(
    c => c.status === 'confirmed' || c.status === 'paid' || c.status === 'payment_completed'
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <img src={logo} alt="Trouve un médiateur" width={128} height={128} className="h-32 w-32 mx-auto mb-4 animate-pulse object-contain" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-background via-muted to-accent/10">
      <SEO 
        canonical="/"
        description="Trouvez des médiateurs qualifiés offrant des consultations en France. Premier site de mise en relation entre médiateurs et parties."
      />
      <OrganizationSchema type="ProfessionalService" />
      <WebsiteSchema />
      <HowToSchema
        name="Comment trouver un médiateur sur Trouve un Médiateur"
        description="Trouvez un médiateur certifié en France en 3 étapes simples pour résoudre vos litiges à l'amiable."
        steps={[
          { name: "Recherchez un médiateur", text: "Sélectionnez votre domaine de médiation (famille, travail, voisinage) et votre localisation pour trouver des médiateurs certifiés près de chez vous." },
          { name: "Consultez les profils", text: "Comparez les profils des médiateurs : certifications, avis clients, disponibilités et tarifs. Choisissez celui qui correspond à votre situation." },
          { name: "Réservez une consultation", text: "Prenez rendez-vous en ligne ou en cabinet en quelques clics. Résolvez votre conflit grâce à un accord de médiation homologable par un juge." },
        ]}
      />
      <AggregateRatingSchema ratingValue={4.8} reviewCount={127} />
      <VideoSchema
        name="Trouve un Médiateur - Présentation de la plateforme de médiation"
        description="Découvrez comment Trouve un Médiateur vous aide à résoudre vos conflits à l'amiable grâce à des médiateurs certifiés en France."
        thumbnailUrl="https://trouveunmediateur.fr/favicon.jpg"
        contentUrl="https://trouveunmediateur.fr/hero-video.mp4"
        uploadDate="2025-01-01"
      />
      
      <Header />

      {/* Mediator Home Section */}
      {user && userRole === 'mediator' && (
        <section className="container mx-auto px-4 pt-8 pb-8 space-y-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">Mes prochains rendez-vous</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                   onClick={() => navigate("/mediateur/tableau-de-bord")}
                >
                  <List className="h-4 w-4 mr-2" />
                  Voir tout
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                   onClick={() => navigate("/mediateur/tableau-de-bord")}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Calendrier
                </Button>
              </div>
            </div>

            {mediatorConsultations && mediatorConsultations.length > 0 ? (
              <div className="space-y-4">
                {mediatorConsultations.map((consultation: any, index: number) => (
                  <ConsultationCard
                    key={consultation.id}
                    consultation={consultation}
                    userType="mediator"
                    highlighted={index === 0}
                    onDetailsClick={() => navigate("/mediateur/tableau-de-bord")}
                  />
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-2">
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Aucun rendez-vous à venir</h3>
                  <p className="text-muted-foreground mb-4">
                    Configurez vos disponibilités pour recevoir des demandes de consultation
                  </p>
                  <Button onClick={() => navigate("/mediateur/tableau-de-bord")}>
                    Gérer mes disponibilités
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="mt-6 flex justify-center">
              <Button 
                onClick={() => navigate("/mediateur/tableau-de-bord")}
                className="bg-primary hover:bg-primary/90"
              >
                Accéder au tableau de bord complet
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Seeker Section */}
      {user && userRole === 'seeker' && (
        <section className="container mx-auto px-4 pt-8 space-y-6">
          {nextConsultation ? (
            <Card className="border-2 border-primary/20 shadow-lg bg-gradient-to-r from-primary/5 to-accent/5">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-full">
                      <Calendar className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-1">Prochaine consultation</h3>
                      <p className="text-muted-foreground mb-2">
                        {nextConsultation.scheduled_date && format(new Date(nextConsultation.scheduled_date), "EEEE d MMMM yyyy", { locale: fr })}
                        {nextConsultation.scheduled_time && ` à ${nextConsultation.scheduled_time}`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {nextConsultation.meeting_type === 'physical' ? '📍 En personne' : '💻 En ligne'}
                        {nextConsultation.location && ` - ${nextConsultation.location}`}
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate("/trouver-un-mediateur")}
                    className="bg-primary hover:bg-primary/90 whitespace-nowrap"
                  >
                    Voir les détails
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <MediatorSearchWidget />
          )}
        </section>
      )}

      {/* Quick Access Card for Connected Seekers */}
      {user && userRole === 'seeker' && (
        <section className="container mx-auto px-4 pt-8">
          <div className="max-w-2xl mx-auto animate-fade-in">
            <Card
              className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/30 cursor-pointer hover-scale"
              onClick={() => navigate("/trouver-un-mediateur")}
            >
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center group-hover:bg-primary/20 transition-all duration-300 group-hover:scale-110">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl text-foreground">Je cherche un médiateur</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Trouvez des médiateurs qualifiés offrant des consultations près de chez vous
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-left space-y-3 mb-6">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="text-muted-foreground">Recherche par localisation et disponibilité</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="text-muted-foreground">Réunion d'information préalable</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="text-muted-foreground">Séances de médiation</span>
                  </li>
                </ul>
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105">
                  Trouver un médiateur
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Hero Section with Background Images */}
      <section className="relative py-12 px-4 overflow-hidden">
        {/* Background Images Carousel */}
        <div className="absolute inset-0 z-0">
          {backgrounds.map((bg, index) => (
            <img
              key={index}
              src={bg}
              alt=""
              role="presentation"
              width={1920}
              height={1080}
              decoding={index === 0 ? "sync" : "async"}
              fetchPriority={index === 0 ? "high" : undefined}
              loading={index === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentBgIndex ? "opacity-50" : "opacity-0"
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/50 to-background/60" />
        </div>

        <div className="container mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-4">
            Trouvez un médiateur
            <span className="block text-primary">juridique certifié</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            Résolvez rapidement vos litiges à l'amiable et évitez le tribunal.
          </p>
          <div className="max-w-4xl mx-auto">
            <p className="text-base text-muted-foreground mb-4">
              <strong className="text-foreground">Trouve un Médiateur</strong> est le premier site français de mise en relation entre particuliers et médiateurs certifiés. Que vous cherchiez à résoudre un conflit de voisinage, un litige au travail ou une situation familiale complexe, nos médiateurs qualifiés vous accompagnent vers une solution à l'amiable, rapide et confidentielle.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/mediation-voisinage" className="text-primary hover:underline font-medium flex items-center gap-1">
                Médiation de voisinage <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/mediation-travail" className="text-primary hover:underline font-medium flex items-center gap-1">
                Médiation du travail <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/mediation-familiale" className="text-primary hover:underline font-medium flex items-center gap-1">
                Médiation familiale <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Comment ça marche */}
      <section id="features" className="py-8 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h3 className="text-3xl font-bold text-foreground mb-4">Comment ça marche ?</h3>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center">
              <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">Vous expliquez votre situation</h4>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">On vous propose des médiateurs adaptés</h4>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center">
                <Handshake className="h-8 w-8 text-primary" />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">Vous trouvez un accord à l'amiable</h4>
            </div>
          </div>

          {/* Video + FAQ + Trust badges */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* FAQ rapide - gauche */}
            <div className="bg-background rounded-xl p-6 border">
              <ul className="space-y-3">
                <li>
                  <Link to="/faq" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
                    <MessageSquare className="h-4 w-4 text-primary flex-shrink-0" />
                    Qu'est-ce qu'un médiateur juridique ?
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
                    <MessageSquare className="h-4 w-4 text-primary flex-shrink-0" />
                    Combien coûte la médiation ?
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
                    <MessageSquare className="h-4 w-4 text-primary flex-shrink-0" />
                    La médiation est-elle vraiment efficace ?
                  </Link>
                </li>
              </ul>
            </div>

            {/* Trust badges - milieu */}
            <div className="bg-background rounded-xl p-6 border space-y-4">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <span className="font-semibold text-foreground text-sm">Médiateurs Certifiés</span>
                  <span className="text-muted-foreground text-sm"> & Indépendants</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <span className="font-semibold text-foreground text-sm">Confidentialité</span>
                  <span className="text-muted-foreground text-sm"> & Neutralité totale</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <span className="font-semibold text-foreground text-sm">Réponse rapide</span>
                  <span className="text-muted-foreground text-sm"> & discrète</span>
                </div>
              </div>
            </div>

            {/* Video - droite */}
            <div className="bg-background rounded-xl overflow-hidden border">
              <div className="aspect-video">
                <video 
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  loop
                  playsInline
                  poster={mediationBg1}
                >
                  <source src={heroVideo} type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Widget for non-authenticated users */}
      {!user && showSearchWidget && (
        <section className="py-8 bg-background">
          <div className="container mx-auto px-4">
            <div ref={searchWidgetRef} className="max-w-4xl mx-auto animate-fade-in">
              <MediatorSearchWidget />
            </div>
          </div>
        </section>
      )}

      {/* Médiation de Voisinage Section */}
      <section className="py-4 bg-muted/30">
        <div className="container mx-auto px-4">
          {/* Hero block */}
          <div className="rounded-2xl overflow-hidden mb-2 border">
            {/* Desktop: two columns - info+cas left, témoignage right */}
            <div className="hidden md:grid md:grid-cols-2 relative">
              <img src={voisinageHero} alt="Médiateur résolvant un conflit de voisinage entre deux voisins en France" width={960} height={640} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-background/80" />
              <div className="relative">
                <div className="relative flex flex-col justify-center p-8">
                  <h3 className="text-4xl font-bold text-foreground mb-2">
                    <span className="text-primary">Médiation</span> de Voisinage
                  </h3>
                  <p className="text-lg font-semibold text-foreground mb-2">
                    Résolvez vos conflits de voisinage sans tribunal
                  </p>
                  <p className="text-muted-foreground mb-4">
                    Bruits, disputes, nuisances… Trouvez une solution apaisée avec l'aide d'un médiateur.
                  </p>
                  <div className="bg-primary/10 rounded-lg px-4 py-2 inline-block mb-4">
                    <span className="font-semibold text-foreground text-sm">Pourquoi choisir la médiation ?</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="text-center">
                      <Clock className="h-6 w-6 text-primary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Plus Rapide</p>
                      <p className="text-xs text-muted-foreground">qu'un procès</p>
                    </div>
                    <div className="text-center">
                      <DollarSign className="h-6 w-6 text-primary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Moins Coûteux</p>
                    </div>
                    <div className="text-center">
                      <Heart className="h-6 w-6 text-primary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Préserve</p>
                      <p className="text-xs text-muted-foreground">les relations</p>
                    </div>
                    <div className="text-center">
                      <Shield className="h-6 w-6 text-primary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Solution</p>
                      <p className="text-xs text-muted-foreground">Durable</p>
                    </div>
                  </div>
                  <div className="border-t border-foreground/10 pt-4">
                    <h4 className="text-lg font-bold text-foreground mb-3">Exemple de Cas</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Voisine âgée dérangée par le bruit</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Jeune famille avec deux enfants</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Médiation organisée avec le médiateur</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Un accord trouvé : horaires ajustés, <strong className="text-foreground">tapis</strong> posés</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="relative p-6 flex flex-col justify-center border-l border-foreground/10">
                {/* Témoignage Client */}
                <div className="flex flex-col items-center text-center">
                  <img src={testimonialClaire} alt="Claire M., cliente satisfaite de la médiation de voisinage" width={56} height={56} loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover mb-2 border-2 border-primary/20" />
                  <h4 className="text-lg font-bold text-foreground mb-2">Témoignage Client</h4>
                  <div className="flex justify-center gap-1 mb-3 text-yellow-500">
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                  <blockquote className="text-sm text-muted-foreground italic mb-2">
                    « Grâce au médiateur, nous avons enfin trouvé un terrain d'entente avec notre voisine. Maintenant, on se salue avec le sourire. »
                  </blockquote>
                  <p className="text-xs text-muted-foreground">— Claire M., Seine-Saint-Denis</p>
                </div>
                <Button onClick={() => { setActiveSearchSection(prev => prev === 'voisinage' ? null : 'voisinage'); setTimeout(() => sectionSearchRefs.current['voisinage']?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }} className="mt-4 w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-105">
                  <Search className="h-4 w-4 mr-2" />
                  Trouver un médiateur
                </Button>
                <Link to="/mediation-voisinage" className="mt-2 text-sm text-primary hover:underline flex items-center justify-center gap-1">
                  En savoir plus sur la médiation de voisinage <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            {/* Mobile: stacked layout */}
            <div className="md:hidden">
              <img src={voisinageHero} alt="Médiateur résolvant un conflit de voisinage entre deux voisins en France" width={640} height={160} loading="lazy" decoding="async" className="w-full h-40 object-cover" />
              <div className="p-4 space-y-2">
                <h3 className="text-2xl font-bold text-foreground">
                  <span className="text-primary">Médiation</span> de Voisinage
                </h3>
                <p className="text-base font-semibold text-foreground">
                  Résolvez vos conflits de voisinage sans tribunal
                </p>
                <p className="text-sm text-muted-foreground">
                  Bruits, disputes, nuisances… Trouvez une solution apaisée avec l'aide d'un médiateur.
                </p>
                <div className="bg-primary/10 rounded-lg px-3 py-1.5 inline-block">
                  <span className="font-semibold text-foreground text-sm">Pourquoi choisir la médiation ?</span>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="text-center">
                    <Clock className="h-5 w-5 text-primary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Plus Rapide</p>
                    <p className="text-xs text-muted-foreground">qu'un procès</p>
                  </div>
                  <div className="text-center">
                    <DollarSign className="h-5 w-5 text-primary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Moins Coûteux</p>
                  </div>
                  <div className="text-center">
                    <Heart className="h-5 w-5 text-primary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Préserve</p>
                    <p className="text-xs text-muted-foreground">les relations</p>
                  </div>
                  <div className="text-center">
                    <Shield className="h-5 w-5 text-primary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Solution</p>
                    <p className="text-xs text-muted-foreground">Durable</p>
                  </div>
                </div>
                {/* Exemple de cas on mobile */}
                <div className="pt-4 border-t mt-4">
                  <h4 className="text-lg font-bold text-foreground mb-3">Exemple de Cas</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Voisine âgée dérangée par le bruit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Jeune famille avec deux enfants</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Médiation organisée avec le médiateur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Un accord trouvé : horaires ajustés, <strong className="text-foreground">tapis</strong> posés</span>
                    </li>
                  </ul>
                </div>
                {/* Témoignage Client mobile */}
                <div className="pt-4 border-t mt-4 flex flex-col items-center">
                  <img src={testimonialClaire} alt="Claire M., cliente satisfaite de la médiation de voisinage" width={56} height={56} loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover mb-2 border-2 border-primary/20" />
                  <h4 className="text-lg font-bold text-foreground mb-2">Témoignage Client</h4>
                  <div className="flex justify-center gap-1 mb-2 text-yellow-500">
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                  <blockquote className="text-sm text-muted-foreground italic mb-2">
                    « Grâce au médiateur, nous avons enfin trouvé un terrain d'entente avec notre voisine. »
                  </blockquote>
                  <p className="text-xs text-muted-foreground">— Claire M., Seine-Saint-Denis</p>
                </div>
                <Button onClick={() => { setActiveSearchSection(prev => prev === 'voisinage' ? null : 'voisinage'); setTimeout(() => sectionSearchRefs.current['voisinage']?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }} className="mt-4 w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-105">
                  <Search className="h-4 w-4 mr-2" />
                  Trouver un médiateur
                </Button>
                <Link to="/mediation-voisinage" className="mt-2 text-sm text-primary hover:underline flex items-center justify-center gap-1">
                  En savoir plus <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
          {activeSearchSection === 'voisinage' && (
            <div ref={(el) => { sectionSearchRefs.current['voisinage'] = el; }} className="mt-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <MediatorSearchWidget defaultMediationArea="immobilier" />
            </div>
          )}
        </div>
      </section>

      {/* Médiation du Travail Section */}
      <section className="py-4 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="rounded-2xl overflow-hidden mb-2 border">
            {/* Desktop: two columns - info+cas left, témoignage right */}
            <div className="hidden md:grid md:grid-cols-2 relative">
              <img src={travailHero} alt="Médiateur spécialisé dans la résolution de conflits au travail en entreprise" width={960} height={640} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-background/85" />
              <div className="relative">
                <div className="relative flex flex-col justify-center p-8">
                  <h3 className="text-4xl font-bold text-foreground mb-2">
                    <span className="text-secondary">Médiation</span> du Travail
                  </h3>
                  <p className="text-lg font-semibold text-foreground mb-2">
                    Résolvez vos conflits professionnels sans prud'hommes
                  </p>
                  <p className="text-muted-foreground mb-4">
                    Harcèlement, licenciement, conditions de travail… Trouvez un accord équitable avec un médiateur spécialisé.
                  </p>
                  <div className="bg-secondary/10 rounded-lg px-4 py-2 inline-block mb-4">
                    <span className="font-semibold text-foreground text-sm">Pourquoi choisir la médiation ?</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="text-center">
                      <Clock className="h-6 w-6 text-secondary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Résolution</p>
                      <p className="text-xs text-muted-foreground">en quelques semaines</p>
                    </div>
                    <div className="text-center">
                      <DollarSign className="h-6 w-6 text-secondary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Économique</p>
                      <p className="text-xs text-muted-foreground">vs avocat</p>
                    </div>
                    <div className="text-center">
                      <Heart className="h-6 w-6 text-secondary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Confidentiel</p>
                      <p className="text-xs text-muted-foreground">et discret</p>
                    </div>
                    <div className="text-center">
                      <Shield className="h-6 w-6 text-secondary mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Accord</p>
                      <p className="text-xs text-muted-foreground">juridiquement valide</p>
                    </div>
                  </div>
                  <div className="border-t border-foreground/10 pt-4">
                    <h4 className="text-lg font-bold text-foreground mb-3">Exemple de Cas</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Salarié en conflit avec son manager</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Tensions sur les conditions de travail</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">3 séances de médiation organisées</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Accord signé : <strong className="text-foreground">aménagement du poste</strong> et plan d'action</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="relative p-6 flex flex-col justify-center border-l border-foreground/10">
                {/* Témoignage Client */}
                <div className="flex flex-col items-center text-center">
                  <img src={testimonialThomas} alt="Thomas R., client satisfait de la médiation du travail" width={56} height={56} loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover mb-2 border-2 border-secondary/20" />
                  <h4 className="text-lg font-bold text-foreground mb-2">Témoignage Client</h4>
                  <div className="flex justify-center gap-1 mb-3 text-yellow-500">
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                  <blockquote className="text-sm text-muted-foreground italic mb-2">
                    « J'étais prêt à démissionner. Le médiateur a su rétablir le dialogue avec mon employeur. Aujourd'hui, je travaille dans de bien meilleures conditions. »
                  </blockquote>
                  <p className="text-xs text-muted-foreground">— Thomas R., Lyon</p>
                </div>
                <Button onClick={() => { setActiveSearchSection(prev => prev === 'travail' ? null : 'travail'); setTimeout(() => sectionSearchRefs.current['travail']?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }} className="mt-4 w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-105">
                  <Search className="h-4 w-4 mr-2" />
                  Trouver un médiateur
                </Button>
                <Link to="/mediation-travail" className="mt-2 text-sm text-primary hover:underline flex items-center justify-center gap-1">
                  En savoir plus sur la médiation du travail <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            {/* Mobile: stacked layout */}
            <div className="md:hidden">
              <img src={travailHero} alt="Médiateur spécialisé dans la résolution de conflits au travail en entreprise" width={640} height={160} loading="lazy" decoding="async" className="w-full h-40 object-cover" />
              <div className="p-4 space-y-2">
                <h3 className="text-2xl font-bold text-foreground">
                  <span className="text-secondary">Médiation</span> du Travail
                </h3>
                <p className="text-base font-semibold text-foreground">
                  Résolvez vos conflits professionnels sans prud'hommes
                </p>
                <p className="text-sm text-muted-foreground">
                  Harcèlement, licenciement, conditions de travail… Trouvez un accord équitable avec un médiateur spécialisé.
                </p>
                <div className="bg-secondary/10 rounded-lg px-3 py-1.5 inline-block">
                  <span className="font-semibold text-foreground text-sm">Pourquoi choisir la médiation ?</span>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="text-center">
                    <Clock className="h-5 w-5 text-secondary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Résolution</p>
                    <p className="text-xs text-muted-foreground">en quelques semaines</p>
                  </div>
                  <div className="text-center">
                    <DollarSign className="h-5 w-5 text-secondary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Économique</p>
                  </div>
                  <div className="text-center">
                    <Heart className="h-5 w-5 text-secondary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Confidentiel</p>
                    <p className="text-xs text-muted-foreground">et discret</p>
                  </div>
                  <div className="text-center">
                    <Shield className="h-5 w-5 text-secondary mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Accord</p>
                    <p className="text-xs text-muted-foreground">juridiquement valide</p>
                  </div>
                </div>
                {/* Exemple de cas on mobile */}
                <div className="pt-4 border-t mt-4">
                  <h4 className="text-lg font-bold text-foreground mb-3">Exemple de Cas</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Salarié en conflit avec son manager</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Tensions sur les conditions de travail</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">3 séances de médiation organisées</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Accord signé : <strong className="text-foreground">aménagement du poste</strong> et plan d'action</span>
                    </li>
                  </ul>
                </div>
                {/* Témoignage Client mobile */}
                <div className="pt-4 border-t mt-4 flex flex-col items-center">
                  <img src={testimonialThomas} alt="Thomas R., client satisfait de la médiation du travail" width={56} height={56} loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover mb-2 border-2 border-secondary/20" />
                  <h4 className="text-lg font-bold text-foreground mb-2">Témoignage Client</h4>
                  <div className="flex justify-center gap-1 mb-2 text-yellow-500">
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                  <blockquote className="text-sm text-muted-foreground italic mb-2">
                    « Le médiateur a su rétablir le dialogue avec mon employeur. Aujourd'hui, je travaille dans de bien meilleures conditions. »
                  </blockquote>
                  <p className="text-xs text-muted-foreground">— Thomas R., Lyon</p>
                </div>
                <Button onClick={() => { setActiveSearchSection(prev => prev === 'travail' ? null : 'travail'); setTimeout(() => sectionSearchRefs.current['travail']?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }} className="mt-4 w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-105">
                  <Search className="h-4 w-4 mr-2" />
                  Trouver un médiateur
                </Button>
                <Link to="/mediation-travail" className="mt-2 text-sm text-primary hover:underline flex items-center justify-center gap-1">
                  En savoir plus <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
          {activeSearchSection === 'travail' && (
            <div ref={(el) => { sectionSearchRefs.current['travail'] = el; }} className="mt-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <MediatorSearchWidget defaultMediationArea="droit-du-travail" />
            </div>
          )}
        </div>
      </section>

      {/* Médiation Familiale Section */}
      <section className="py-4 bg-muted/40">
        <div className="container mx-auto px-4">
          <div className="rounded-2xl overflow-hidden mb-2 border">
            {/* Desktop: two columns - info+cas left, témoignage right */}
            <div className="hidden md:grid md:grid-cols-2 relative">
              <img src={familleHero} alt="Médiateur familial accompagnant une famille dans la résolution de conflits" width={960} height={640} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-background/85" />
              <div className="relative">
                <div className="relative flex flex-col justify-center p-8">
                  <h3 className="text-4xl font-bold text-foreground mb-2">
                    <span className="text-destructive">Médiation</span> Familiale
                  </h3>
                  <p className="text-lg font-semibold text-foreground mb-2">
                    Protégez votre famille, trouvez un accord à l'amiable
                  </p>
                  <p className="text-muted-foreground mb-4">
                    Divorce, garde d'enfants, succession… Un médiateur vous aide à trouver des solutions respectueuses pour toute la famille.
                  </p>
                  <div className="bg-destructive/10 rounded-lg px-4 py-2 inline-block mb-4">
                    <span className="font-semibold text-foreground text-sm">Pourquoi choisir la médiation ?</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="text-center">
                      <Heart className="h-6 w-6 text-destructive mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Préserve</p>
                      <p className="text-xs text-muted-foreground">les liens familiaux</p>
                    </div>
                    <div className="text-center">
                      <Shield className="h-6 w-6 text-destructive mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Intérêt</p>
                      <p className="text-xs text-muted-foreground">des enfants</p>
                    </div>
                    <div className="text-center">
                      <Clock className="h-6 w-6 text-destructive mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Plus rapide</p>
                      <p className="text-xs text-muted-foreground">qu'un tribunal</p>
                    </div>
                    <div className="text-center">
                      <DollarSign className="h-6 w-6 text-destructive mx-auto mb-1" />
                      <p className="text-xs font-semibold text-foreground">Économique</p>
                      <p className="text-xs text-muted-foreground">et humain</p>
                    </div>
                  </div>
                  <div className="border-t border-foreground/10 pt-4">
                    <h4 className="text-lg font-bold text-foreground mb-3">Exemple de Cas</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Couple en séparation avec deux enfants</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Désaccord sur la garde et le partage des biens</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">4 séances de médiation sur 2 mois</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Accord trouvé : <strong className="text-foreground">garde alternée</strong> et convention parentale signée</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="relative p-6 flex flex-col justify-center border-l border-foreground/10">
                {/* Témoignage Client */}
                <div className="flex flex-col items-center text-center">
                  <img src={testimonialSophie} alt="Sophie D., cliente satisfaite de la médiation familiale" width={56} height={56} loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover mb-2 border-2 border-destructive/20" />
                  <h4 className="text-lg font-bold text-foreground mb-2">Témoignage Client</h4>
                  <div className="flex justify-center gap-1 mb-3 text-yellow-500">
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                  <blockquote className="text-sm text-muted-foreground italic mb-2">
                    « La médiation nous a permis de trouver un accord pour nos enfants sans passer par un juge. Aujourd'hui, nous communiquons beaucoup mieux. »
                  </blockquote>
                  <p className="text-xs text-muted-foreground">— Sophie et Marc D., Bordeaux</p>
                </div>
                <Button onClick={() => { setActiveSearchSection(prev => prev === 'famille' ? null : 'famille'); setTimeout(() => sectionSearchRefs.current['famille']?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }} className="mt-4 w-full bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-105">
                  <Search className="h-4 w-4 mr-2" />
                  Trouver un médiateur
                </Button>
                <Link to="/mediation-familiale" className="mt-2 text-sm text-primary hover:underline flex items-center justify-center gap-1">
                  En savoir plus sur la médiation familiale <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            {/* Mobile: stacked layout */}
            <div className="md:hidden">
              <img src={familleHero} alt="Médiateur familial accompagnant une famille dans la résolution de conflits" width={640} height={160} loading="lazy" decoding="async" className="w-full h-40 object-cover" />
              <div className="p-4 space-y-2">
                <h3 className="text-2xl font-bold text-foreground">
                  <span className="text-destructive">Médiation</span> Familiale
                </h3>
                <p className="text-base font-semibold text-foreground">
                  Protégez votre famille, trouvez un accord à l'amiable
                </p>
                <p className="text-sm text-muted-foreground">
                  Divorce, garde d'enfants, succession… Un médiateur vous aide à trouver des solutions respectueuses pour toute la famille.
                </p>
                <div className="bg-destructive/10 rounded-lg px-3 py-1.5 inline-block">
                  <span className="font-semibold text-foreground text-sm">Pourquoi choisir la médiation ?</span>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="text-center">
                    <Heart className="h-5 w-5 text-destructive mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Préserve</p>
                    <p className="text-xs text-muted-foreground">les liens familiaux</p>
                  </div>
                  <div className="text-center">
                    <Shield className="h-5 w-5 text-destructive mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Intérêt</p>
                    <p className="text-xs text-muted-foreground">des enfants</p>
                  </div>
                  <div className="text-center">
                    <Clock className="h-5 w-5 text-destructive mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Plus rapide</p>
                    <p className="text-xs text-muted-foreground">qu'un tribunal</p>
                  </div>
                  <div className="text-center">
                    <DollarSign className="h-5 w-5 text-destructive mx-auto mb-1" />
                    <p className="text-xs font-semibold text-foreground">Économique</p>
                  </div>
                </div>
                <div className="pt-4 border-t mt-4">
                  <h4 className="text-lg font-bold text-foreground mb-3">Exemple de Cas</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Couple en séparation avec deux enfants</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Désaccord sur la garde et le partage des biens</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">4 séances de médiation sur 2 mois</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Accord trouvé : <strong className="text-foreground">garde alternée</strong> et convention parentale</span>
                    </li>
                  </ul>
                </div>
                <div className="pt-4 border-t mt-4 flex flex-col items-center">
                  <img src={testimonialSophie} alt="Sophie D., cliente satisfaite de la médiation familiale" width={56} height={56} loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover mb-2 border-2 border-destructive/20" />
                  <h4 className="text-lg font-bold text-foreground mb-2">Témoignage Client</h4>
                  <div className="flex justify-center gap-1 mb-2 text-yellow-500">
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                    <Star className="h-4 w-4 fill-current" />
                  </div>
                  <blockquote className="text-sm text-muted-foreground italic mb-2">
                    « La médiation nous a permis de trouver un accord pour nos enfants sans passer par un juge. »
                  </blockquote>
                  <p className="text-xs text-muted-foreground">— Sophie et Marc D., Bordeaux</p>
                </div>
                <Button onClick={() => { setActiveSearchSection(prev => prev === 'famille' ? null : 'famille'); setTimeout(() => sectionSearchRefs.current['famille']?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }} className="mt-4 w-full bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-105">
                  <Search className="h-4 w-4 mr-2" />
                  Trouver un médiateur
                </Button>
                <Link to="/mediation-familiale" className="mt-2 text-sm text-primary hover:underline flex items-center justify-center gap-1">
                  En savoir plus <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
          {activeSearchSection === 'famille' && (
            <div ref={(el) => { sectionSearchRefs.current['famille'] = el; }} className="mt-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <MediatorSearchWidget defaultMediationArea="droit-de-la-famille" />
            </div>
          )}
        </div>
      </section>

      {/* Mediator CTA Section */}
      {!user && (
        <section className="py-8 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <Card
                className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-accent/30 cursor-pointer hover-scale"
                onClick={() => navigate("/mediateur")}
              >
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl text-foreground">Vous êtes médiateur.trice ?</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Développez votre activité de médiation et gérez vos clients efficacement
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="text-left space-y-3 mb-6">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Mise en relation avec les clients</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Gestion automatique du processus de médiation</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Développement du réseau et de la visibilité</span>
                    </li>
                  </ul>
                  <Button className="w-full bg-accent hover:bg-accent/90 text-accent-foreground py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105">
                    Inscrivez-vous sur Trouve un Médiateur
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
};

export default Index;
